import React, {useState}    from "react"
import {useNavigate}        from "react-router-dom"
import {
	Box,
	Divider,
	IconButton,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Switch,
} from "@mui/material"
import {
	AccountCircleOutlined,
	AdminPanelSettings,
	BugReport,
	GridViewOutlined,
	HomeOutlined,
	ListAlt,
	Logout,
	Menu as MenuIcon,
	ShoppingBagOutlined,
	Support,
	SwitchAccount,
	YouTube,
} from "@mui/icons-material"
import ThemeToggle          from "./ThemeToggle"
import useUserRoles         from "../hooks/useUserRoles"
import {useUserStore}       from "../state"
import {restoreAdminSession} from "../util/admin"
import {usePromptDebug}      from "./debug/PromptDebugProvider"

/*
 * The hamburger and its menu (080).
 *
 * Extracted from Navbar so study mode can show the SAME control rather than a copy of it —
 * this menu carries admin, impersonation, support and sign-out branches, and a second
 * signed-out variant, none of which should exist twice.
 *
 * Owns its own anchor state and reads the user itself; toggleTheme is the only prop, because
 * the theme control lives inside the menu.
 */
const NavMenuButton = ({toggleTheme}) => {

	const navigate = useNavigate()
	const {user, signOut} = useUserStore()
	const isAdmin = useUserRoles(['admin'])
	const promptDebug = usePromptDebug()
	const adminSession = localStorage.getItem('admin_session')

	const [navMenuAnchorEl, setNavMenuAnchorEl] = useState(null)

	const handleNavMenuClick = (event) => {
		setNavMenuAnchorEl(event.currentTarget)
	}
	const handleNavMenuClose = () => {
		setNavMenuAnchorEl(null)
	}

	const logout = () => {
		signOut()
	}

	const login = () => {
		navigate('/login')
	}

	const createAccount = () => {
		navigate('/login/signUp')
	}

	const launchSupportEmail = () => {
		const currentUrl = window.location.href
		const body = `



------------------------------------------
Page: ${currentUrl}
User: ${user.email}
Identifier: ${user.id}
`
		const mailtoLink = `mailto:support@birdsy.ai?subject=Birdsy%20Support&body=${encodeURIComponent(body)}`
		window.location.href = mailtoLink
	}

	return (
		<>
			<IconButton
				size="large"
				onClick={handleNavMenuClick}
				aria-controls="nav-menu"
				aria-haspopup="true"
			>
				<MenuIcon />
			</IconButton>

			{
				user
				? <Menu
					anchorEl={navMenuAnchorEl}
					id="nav-menu"
					open={(navMenuAnchorEl !== null)}
					onClose={handleNavMenuClose}
					onClick={handleNavMenuClose}
					transformOrigin={{horizontal: 'right', vertical: 'top'}}
					anchorOrigin={{horizontal: 'right', vertical: 'bottom'}}
				  >
					{
						adminSession &&
						<MenuItem onClick={async () => {
								await restoreAdminSession()
								window.location.reload()
							}
						}>
							<ListItemIcon>
								<SwitchAccount color={"error"} fontSize="small"/>
							</ListItemIcon>
							<ListItemText primary="Stop Impersonating"/>
						</MenuItem>
					}
					<MenuItem onClick={() => navigate(`/prepkit`)}>
						<ListItemIcon>
							<GridViewOutlined fontSize="small"/>
						</ListItemIcon>
						<ListItemText primary="Dashboard"/>
					</MenuItem>
					{
						isAdmin && <Box>
							<Divider/>
							<MenuItem
								key="mnu-admin"
								onClick={() => navigate('/admin')}>
								<ListItemIcon>
									<AdminPanelSettings fontSize="small"/>
								</ListItemIcon>
								<ListItemText primary="Admin"/>
							</MenuItem>
							{/*
							  * Prompt debug mode (097). Stays open on click -- it is a switch, not a
							  * destination, and closing the menu to read the state back is friction.
							  * The server checks the admin role itself; this only sets a header.
							  */}
							<MenuItem
								key="mnu-debug-prompts"
								onClick={(evt) => {
									evt.stopPropagation()
									promptDebug.setEnabled(!promptDebug.enabled)
								}}>
								<ListItemIcon>
									<BugReport fontSize="small"/>
								</ListItemIcon>
								<ListItemText primary="Debug prompts"/>
								<Switch size="small" checked={promptDebug.enabled} readOnly/>
							</MenuItem>
						</Box>
					}


					<Divider/>

					<MenuItem
						onClick={launchSupportEmail}
					>
						<ListItemIcon>
							<Support color={"info"} />
						</ListItemIcon>
						<ListItemText primary="Support" />
					</MenuItem>

					<MenuItem onClick={(evt)=>evt.stopPropagation()}>
						<ThemeToggle icon={true} toggleTheme={toggleTheme}/>
					</MenuItem>

						<Divider/>

						{user &&
							<MenuItem onClick={() => navigate(`/account/profile`)}>
								<ListItemIcon>
									<AccountCircleOutlined fontSize="small"/>
								</ListItemIcon>
								<ListItemText primary="Profile" secondary={user.email}/>

							</MenuItem>
						}
						{user &&
							<MenuItem onClick={logout}>
								<ListItemIcon>
									<Logout fontSize="small"/>
								</ListItemIcon>
								<ListItemText primary="Sign Out"/>
							</MenuItem>
						}

				  </Menu>
				: <Menu
						anchorEl={navMenuAnchorEl}
						id="nav-menu"
						open={(navMenuAnchorEl !== null)}
						onClose={handleNavMenuClose}
						onClick={handleNavMenuClose}
						transformOrigin={{horizontal: 'right', vertical: 'top'}}
						anchorOrigin={{horizontal: 'right', vertical: 'bottom'}}
					>
						<MenuItem onClick={login}>
							<ListItemText primary="Login"/>
						</MenuItem>

						<MenuItem onClick={createAccount}>
							<ListItemText primary="Create an Account"/>
						</MenuItem>

						<MenuItem onClick={(evt)=>evt.stopPropagation()}>
							<ThemeToggle icon={true} toggleTheme={toggleTheme}/>
						</MenuItem>
					</Menu>
			}
		</>
	)
}

export default NavMenuButton
